/**
 * Para onde mandar alguém: a primeira tela que a pessoa PODE ver.
 *
 * Existe porque as rotas de FinOps, Orçamento e Administração passaram a ter
 * guarda (14/10/2026). Com guarda, um destino fixo vira armadilha: um grupo só de
 * Orçamento, mandado para `finops/dashboard`, seria devolvido a `/` — que o
 * mandaria de novo para `finops/dashboard`, em círculo. Todo destino automático
 * (`/`, o índice do cliente, o índice de cada módulo) sai daqui.
 *
 * Mesma regra de visibilidade da barra lateral: permissão `ver` no módulo E flag
 * do módulo ligada. Ausência de flag conta como ligada (`useFlag`).
 */
import { basePath, MODULOS, type Aba, type Modulo } from '@/app/navigation'
import type { Flags } from '@/flags/flags'
import { acessoDe, type MapaPermissoes } from '@/permissions/resolver'

export function moduloVisivel(modulo: Modulo, mapa: MapaPermissoes, flags: Flags): boolean {
  return acessoDe(mapa, modulo.permissao).ver && (flags[modulo.flag] ?? true)
}

/**
 * A primeira aba do módulo com `ver`. As abas do escopo do cliente vêm antes das
 * globais: no Compliance, quem pode as duas cai no Questionário (o índice do módulo
 * sempre foi ele), e não em Perguntas do padrão, que fica fora do cliente.
 */
export function primeiraAbaVisivel(modulo: Modulo, mapa: MapaPermissoes): Aba | undefined {
  const ordem = [...modulo.abas.filter((aba) => !aba.global), ...modulo.abas.filter((aba) => aba.global)]
  return ordem.find((aba) => acessoDe(mapa, aba.permissao).ver)
}

function caminhoDaAba(modulo: Modulo, aba: Aba, idCliente: string | null): string | null {
  if (aba.global) return `/${modulo.id}/${aba.segmento}`
  const base = basePath(modulo, idCliente)
  return base && `${base}/${aba.segmento}`
}

/**
 * O caminho da primeira tela visível, na ordem da barra lateral — ou `null` quando
 * não há nenhuma. `null` é para a tela DIZER isso; redirecionar seria o círculo.
 */
export function primeiraTelaVisivel(
  mapa: MapaPermissoes,
  flags: Flags,
  idCliente: string | null,
): string | null {
  for (const modulo of MODULOS) {
    if (!moduloVisivel(modulo, mapa, flags)) continue
    const aba = primeiraAbaVisivel(modulo, mapa)
    const caminho = aba && caminhoDaAba(modulo, aba, idCliente)
    if (caminho) return caminho
  }
  return null
}
